<i18n lang="yaml">
en:
  viewDetails: "View Details"
  agencies: "no agencies | {n} agency | {n} agencies"
  viewOnMap: "View on map"
  loadMore: "Load more"
  showing: "Showing {count} / {total}"
  notFound: "Matched no property"
  searchResults: "Results"
  filters: "Filters"
  fields:
    price: "Price"
    rent: "Rent"
    sqm: "Area"
ja:
  viewDetails: "物件詳細を見る"
  agencies: "取扱店舗{n}社"
  viewOnMap: "マップで見る"
  loadMore: "もっとみる"
  showing: "{count}件表示 / {total}件中"
  notFound: "物件が見つかりませんでした"
  searchResults: "検索結果"
  filters: "絞り込み"
  fields:
    price: "価格"
    rent: "賃料"
    sqm: "広さ"
</i18n>
<script setup lang="ts">
import { ref, watch } from "vue";
import { storeToRefs } from "pinia";

import Sentry from "@/modules/sentry";
import LayoutHeader from "@/components/LayoutHeader.vue";
import LayoutBreadcrumb from "@/components/LayoutBreadcrumb.vue";
import LayoutFooter from "@/components/LayoutFooter.vue";
import UIProperty from "@/components/ui/Property.vue";
import UISpinner from "@/components/ui/Spinner.vue";
import IconDown from "@/assets/icons/arrow-down.svg?component";

import type { Property } from "@/types/properties.d";
import type { UserProfile, OrganizationProfile } from "@/types/profiles.d";

import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useHead } from "@unhead/vue";
import type { MessageContext } from "@intlify/core-base";
import { use as usePageLevel } from "@/stores/pageLevel";

import { useListInit } from "@/composables/list";
import { useAPI } from "@/composables/api";

const route = useRoute();
const router = useRouter();
const api = useAPI();

import type { Filters } from "@/stores/list";

const page = usePageLevel();
const { pageLevel } = storeToRefs(page);

const { t } = useI18n({
  messages: {
    ja: {
      title: (ctx: MessageContext) => {
        const { type, buildingType } = ctx.values;
        if (!type || !buildingType) return "";
        return `${ctx.linked(
          "currentLocation",
          ctx.values
        )}の${type}${buildingType}の物件チラシを探す`;
      },
      description: (ctx: MessageContext) => {
        const { type, buildingType } = ctx.values;
        if (!type || !buildingType) return "";
        return `${ctx.linked(
          "currentLocation",
          ctx.values
        )}の${type}${buildingType}物件の一覧です。図面を押すと拡大して表示できます。`;
      },
    },
  },
});

const {
  loading,
  load,
  filters,
  properties,
  area,
  more: moreResults,
  tagGroups,
} = await useListInit(async (filters: Filters, skip: number) => {
  if (
    typeof route.params.levels == "string" ||
    route.params.levels.length < 2
  ) {
    await router.replace({ name: "notFound" });
    return null;
  }

  const res = await api.call(
    "list",
    route.params.levels.join("/"),
    filters,
    20,
    skip
  );
  return res.data;
});

// brokers
interface AgentResponse {
  agent: UserProfile;
  company: OrganizationProfile;
}
const agents = ref<Record<string, AgentResponse>>({});
const companies = ref<Record<string, OrganizationProfile>>({});
watch(properties, (arr) => {
  const flatAgents = arr.map((item) => item.agents).flat();
  const promises = [];
  for (const { id, organizationId } of flatAgents) {
    if (!(id in agents.value)) {
      promises.push(
        api
          .call<AgentResponse>("agent", id, organizationId)
          .then(({ data }) => {
            agents.value[id] = data;
            companies.value[organizationId] = data.company;
          })
      );
    }
  }
  Promise.all(promises).catch(Sentry.captureException);
});
function getAgencies(property: Property) {
  return new Set(property.agents.map((item) => item.organizationId));
}

function onCheck(event: Event) {
  const target = <HTMLInputElement>event.target;
  if (target) {
    const { checked, value } = target;
    if (checked) {
      filters.value.labels = [...new Set(filters.value.labels.concat(value))];
    } else {
      filters.value.labels.splice(filters.value.labels.indexOf(value), 1);
    }
  }
}

const sidebar = ref(false);

useHead({
  title: () => t("title", pageLevel.value),
  meta: [
    { name: "description", content: () => t("description", pageLevel.value) },
    { name: "og:title", content: () => t("title", pageLevel.value) },
    {
      name: "og:description",
      content: () => t("description", pageLevel.value),
    },
  ],
  link: [
    { rel: "canonical", href: import.meta.env.VITE_SEO_ORIGIN + route.path },
  ],
});
</script>
<template>
  <LayoutHeader />
  <nav class="tags-section" v-if="false">
    <div class="tags frame">
      <div class="row u-row">
        <a class="tag small w6" v-for="(i, j) of [, , , , , , , , , ,]" :key="j"
          >🔥 話題タグ</a
        >
      </div>
      <div class="row u-row">
        <a class="tag small w6" v-for="(i, j) of [, , , , ,]" :key="j"
          >🚩 注目タグ {{ j + 1 }}</a
        >
      </div>
    </div>
  </nav>
  <LayoutBreadcrumb />
  <main class="reset">
    <div class="frame">
      <section class="list-wrapper u-col u-flex">
        <!--
        <div>
          <h1 class="t-center">{{ title }}で物件を探す</h1>
          <div class="lines" v-if="route.params.areaType == 'station'">
            <ul class="u-row" v-if="area">
              <li v-for="line of area.lines" :key="line.id">{{ line.name }}</li>
            </ul>
          </div>
        </div>
        -->
        <div class="head" v-if="pageLevel">
          <h1 class="w6">{{ t("title", pageLevel) }}</h1>
          <h2>{{ t("description", pageLevel) }}</h2>
        </div>
        <div class="result-summary u-row">
          <h2 class="h4" v-t="'searchResults'" />
          <i18n-t keypath="showing" tag="div" class="count" v-if="area">
            <template #count>
              <span class="h4 w7 cl-blue">{{ properties.length }}</span>
            </template>
            <template #total>{{ area.dealsCount ?? 0 }}</template>
          </i18n-t>
        </div>
        <div
          class="list not-found"
          v-if="area && !area.dealsCount"
          v-t="'notFound'"
        />
        <UISpinner class="u-center" v-else-if="loading" />
        <div class="list u-col" v-else>
          <UIProperty
            v-for="property of properties"
            :key="property.id"
            :property="property"
          >
            <template #bottom>
              <div class="agents u-row">
                <div class="u-row">
                  <template v-for="id of getAgencies(property)" :key="id">
                    <div
                      class="agent u-row"
                      v-if="id in companies && companies[id].general"
                    >
                      <div
                        class="icon"
                        :style="{
                          backgroundImage:
                            'url(' +
                            (companies[id].general?.organizationImageFile
                              ?.url ?? '/images/avatar.png') +
                            ')',
                        }"
                        :alt="companies[id].general?.name"
                        :title="companies[id].general?.name"
                      />
                      <!--
                        <span class="company">
                          {{ agents[agent.id].company.name }}
                        </span>
                        <span class="name">{{ agents[agent.id].fullname }}</span>
                        -->
                    </div>
                  </template>
                  <i18n-t
                    tag="div"
                    class="sum"
                    keypath="agencies"
                    :plural="getAgencies(property).size"
                  >
                    <template #n
                      ><b class="h4 w6">{{
                        getAgencies(property).size
                      }}</b></template
                    >
                  </i18n-t>
                </div>
              </div>
            </template>
          </UIProperty>
          <div
            class="more u-row"
            v-if="moreResults"
            @click="load({ more: true })"
          >
            <button type="button" v-t="'loadMore'" />
          </div>
        </div>
      </section>
      <section class="side u-col" :class="{ open: sidebar }">
        <div class="filter">
          <h2 class="h4 w6 u-row">
            <span class="u-flex" v-t="'filters'" />
            <button type="button" class="mobile" @click="sidebar = !sidebar">
              <IconDown />
            </button>
          </h2>
          <div
            class="group u-col"
            v-for="[group, tags] of tagGroups"
            :key="group"
          >
            <h3 class="label h4 w6">{{ group }}</h3>
            <div class="tags u-col">
              <div class="tag u-row" v-for="tag of tags" :key="tag.id">
                <input
                  type="checkbox"
                  :name="tag.id"
                  :id="tag.id"
                  :value="tag.id"
                  @change="onCheck"
                />
                <label class="u-flex" :for="tag.id">{{ tag.id }}</label>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </main>
  <LayoutFooter />
</template>
<style scoped>
main {
  padding-bottom: 40px;
}
main .u-col,
main .u-row {
  align-items: start;
}
.frame {
  flex-direction: row-reverse;
}
.tags-section {
  width: 100%;
  background: var(--cl-blue);
  overflow-x: auto;
}
.tags-section .tags {
  margin: 16px auto;
}
.tags-section .tags .tag {
  padding: 10px;
  background: var(--cl-white);
  line-height: 12px;
  flex-shrink: 0;
}
.tags-section .tags .row:not(:last-child) .tag {
  margin-bottom: 16px;
}
.tags-section .tags .row .tag:not(:last-child) {
  margin-right: 16px;
}
main {
  width: 100%;
  padding-top: 32px;
  align-items: start;
}
main > .frame {
  display: flex;
}
@media (max-width: 1060px) {
  main {
    margin-top: 0;
    padding-top: 0;
  }
  main > .frame {
    flex-direction: column-reverse;
  }
  .tags-section .tags {
    margin: 16px 5%;
  }
  .list-wrapper {
    flex: unset;
    overflow: unset;
    padding-top: 24px;
  }
  .list-wrapper .result-summary {
    margin-top: 24px;
  }
  .list-wrapper .list .property {
    flex-direction: column;
    padding: 24px 0;
  }
  .list-wrapper .list .property .image img {
    width: 100%;
  }
  .list-wrapper .list .property .summary {
    margin: 16px 0 0 0;
    width: 100%;
    overflow: hidden;
  }
  .list-wrapper .list .property .summary .bottom {
    flex-direction: column;
    align-items: end;
  }
  .list-wrapper .list .property .summary .bottom .detail {
    width: 100%;
    height: 40px;
    margin-top: 16px;
  }
}
main .head h2 {
  margin-top: 12px;
  font-size: 14px;
  font-weight: 300;
  color: var(--cl-black-60);
}
.side {
  min-width: 215px;
  margin-right: 35px;
}
.side button {
  width: 100%;
  position: relative;
}
.side button .btn {
  position: absolute;
}
.side .filter .u-col {
  align-items: start;
}
.side .filter .h4 {
  margin: 0;
}
.side .filter .group {
  margin-top: 24px;
}
.side .filter .group .tags.wrap {
  max-height: 170px;
  flex-wrap: wrap;
}
.side .filter .group .tags .tag {
  align-items: center;
  margin-top: 12px;
}
.side .filter .group .tags .tag label {
  margin-left: 4px;
}
@media (max-width: 1060px) {
  .side {
    padding: 16px 16px;
    margin-left: -16px;
    margin-right: -16px;
    border-bottom: solid 1px var(--cl-black-10);
  }
  .side .filter {
    width: 100%;
  }
  .side .filter h2 button {
    width: 24px;
    height: 24px;
  }
  .side .filter h2 svg {
    width: 24px;
    height: 24px;
  }
  .side:not(.open) .group {
    display: none;
  }
}
.lines li + li {
  margin-left: 8px;
}
.list-wrapper .result-summary {
  margin-top: 48px;
  margin-bottom: 24px;
}
.list-wrapper .result-summary .count {
  margin-left: 12px;
}
.list-wrapper .result-summary .count span {
  margin-right: 2px;
}
.list {
  border-top: solid 1px var(--cl-black-10);
  width: 100%;
  align-items: start;
}
.list.not-found {
  padding-top: 24px;
}
.list .property:not(:last-child) {
  border-bottom: solid 1px var(--cl-black-10);
}
.list .property:deep(.image img) {
  min-width: 350px;
  width: 350px;
}
.list .tags {
  flex-wrap: wrap;
  margin-top: -8px;
  margin-bottom: 24px;
}
.list .tags .tag {
  border-radius: 3px;
  border: 1px solid var(--base-black-20, #ced1cf);
  background: var(--base-black-00, #fff);
  padding: 0 8px;
  height: 28px;
  line-height: 28px;
  font-size: 12px;
  margin-right: 8px;
  margin-top: 8px;
  flex-shrink: 0;
}
.list .property .bottom .agents .u-row {
  align-items: center;
}
.list .property .bottom .detail {
  margin-left: auto;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  height: 40px;
}
/deep/ .list .property .bottom .agents .label {
  line-height: 25px;
}
/deep/ .list .property .bottom .agents .agent {
  align-items: center;
}
/deep/ .list .property .bottom .agents .agent:not(:first-child) {
  margin-left: -16px;
}
/deep/ .list .property .bottom .agents .agent .icon {
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  height: 32px;
  width: 32px;
  margin-right: 8px;
}
/deep/ .list .property .bottom .agents .agent .company {
  margin-right: 8px;
}
/deep/ .list .property .bottom .agents .sum b {
  margin: 0 1px;
}
.list .more {
  margin: 48px auto;
}
.list .more button {
  padding: 12px 24px;
  border: 1px solid var(--cl-black);
  box-shadow: 0px 2px 10px 2px rgba(30, 30, 30, 0.1);
  border-radius: 4px;
}
.map {
  margin-top: 30px;
  height: 400px;
  width: 100%;
}
.frame,
:deep(.frame) {
  width: 1060px;
  max-width: 90%;
  margin: 0 auto;
}
@media (max-width: 768px) {
  .frame {
    margin: 0 16px;
    width: auto;
    max-width: unset;
  }
}
@media (max-width: 1060px) {
  .property >>> .summary .bottom {
    flex-direction: column;
    align-items: end;
  }
  .property >>> .summary .bottom .detail {
    width: 100%;
    height: 40px;
    margin-top: 16px;
  }
}
</style>
<style>
body {
  overflow-y: auto;
}
h2,
h3,
h4,
h5 {
  font-size: unset;
  font-weight: unset;
  font-style: unset;
  margin: 0;
}
</style>
